import { Inject, Injectable, OnDestroy } from '@angular/core';
import { filter, fromEvent, map, Observable, Subject, takeUntil } from 'rxjs';

import { FhcoMessage, FhcoOutboundMessageType } from './fhco-post-message.model';
import { WINDOW } from './window.token';

@Injectable({
  providedIn: 'root'
})
export class FhcoPostMessageService implements OnDestroy {

  private readonly destroyed$ = new Subject<void>();
  private readonly messagesSubject = new Subject<FhcoMessage>();
  readonly messages$: Observable<FhcoMessage> = this.messagesSubject.asObservable();

  constructor(@Inject(WINDOW) private readonly windowRef: Window) {
    fromEvent<MessageEvent>(this.windowRef, 'message')
      .pipe(
        filter((event) => event.source === this.windowRef.parent),
        map((event) => event.data),
        map((data) => this.toFhcoMessage(data)),
        filter((message): message is FhcoMessage => message !== null),
        takeUntil(this.destroyed$)
      )
      .subscribe((message) => this.messagesSubject.next(message));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  isEmbedded(): boolean {
    return this.windowRef.parent !== this.windowRef;
  }

  send(type: FhcoOutboundMessageType, content: unknown): void {
    if (!this.isEmbedded()) {
      return;
    }

    this.windowRef.parent.postMessage({
      type,
      version: '1.0.0',
      content
    }, '*');
  }

  private toFhcoMessage(data: unknown): FhcoMessage | null {
    if (typeof data !== 'object' || data === null) {
      return null;
    }

    const candidate = data as Partial<FhcoMessage>;
    if (typeof candidate.type === 'string' && 'content' in candidate) {
      return candidate as FhcoMessage;
    }

    return {
      type: 'prefill',
      version: '1.0.0',
      content: data
    };
  }
}
